import React, { useState, useEffect } from 'react'
import { fromZonedTime } from 'date-fns-tz'
import { nextDay, startOfDay } from 'date-fns'
import { startCase } from 'lodash'
import { Warning } from '@phosphor-icons/react'
import ReactTooltip from 'react-tooltip'
import Select, { components as selectComponents } from 'react-select'
import { useTranslation } from 'react-i18next'

import CheckboxField from '../FormLegacy/FormFieldsV2/CheckboxField'
import ToggleField from '../FormLegacy/FormFieldsV2/ToggleField'
import DateField from '../FormLegacy/FormFieldsV2/DateField'
import SelectField from '../FormLegacy/FormFieldsV2/SelectField'
import TextField from '../FormLegacy/FormFieldsV2/TextField'
import Badge from '../Badge/Badge'
import Filter from '../Filter/Filter'
import SelectDropdownInput from '../SelectDropdownInput/SelectDropdownInput'
import { formatTimezoneDate } from '../../helpers/helpers'
import { getAssetLink } from '../../helpers/get-asset-link'
import API from '../../api/api'
import {
  LOCATION_TYPES,
  buildLocationValuePayload,
  getUnitSelectOptions,
  resolveUnitSelection,
  toUnitArray,
  toUnitOption,
} from './unitSelection'

export const MAX_STAFF_OPTIONS = 5

const MAX_VISIBLE_UNIT_CHIPS = 3

// Note: collapses the chip list so selecting every unit doesn't stretch the field. The count
// comes from selectProps.hiddenUnitsCount, not getValue() — getValue is a stable per-instance
// closure, so the compiler caches its result here and the count freezes at its first value.
const UnitMultiValue = (props) => {
  const { t } = useTranslation(['internal-inspections'])
  const { index, selectProps } = props

  if (index < MAX_VISIBLE_UNIT_CHIPS) return <selectComponents.MultiValue {...props} />
  if (index > MAX_VISIBLE_UNIT_CHIPS) return null

  return (
    <div className="units-overflow-count">
      {t('internal-inspections:locationInput.moreUnits', {
        count: selectProps.hiddenUnitsCount,
      })}
    </div>
  )
}

// Note: large enough to cover any building in one request — the multi-unit picker
// filters client-side, so the full list is fetched once per building/phase.
const ALL_UNITS_FETCH_LIMIT = 10000

export const fetchAllUnitOptions = async (buildingId, phase = '') => {
  try {
    const {
      data: { data = [] },
    } = await API.getUnits(buildingId, 0, ALL_UNITS_FETCH_LIMIT, '', phase)

    return data.map(({ unit }) => toUnitOption(unit))
  } catch (error) {
    console.error(error)
    return []
  }
}

export const INSPECTION_ENTITY_TYPE = {
  SETUP: 'setup',
  SCHEDULED: 'scheduled',
  TEMPLATE: 'template',
}

export const CREATE_FROM = {
  TEMPLATE: 'template',
  BLANK: 'blank',
}

export const TRIGGER_OPTIONS = {
  ONE_TIME: 'one-time',
  RECURRING: 'recurring',
}

export const INSPECTION_STATUS = {
  ALL: '',
  STARTED: 'in-progress',
  UPCOMING: 'created',
  OVERDUE: 'over-due',
  DUE_TODAY: 'due-today',
  COMPLETED: 'completed',
}

export const TRIGGER_TYPES = {
  TIME: 'time',
  TRIGGER: 'trigger',
}

export const RECURRING_RECURRENCE_TYPES = ['daily', 'weekly', 'monthly', 'yearly', 'trigger']

export const getInspectionsListPath = (trigger) =>
  trigger === TRIGGER_OPTIONS.RECURRING
    ? '/dashboard/building-dashboard/internal-inspections/setup/recurring'
    : '/dashboard/building-dashboard/internal-inspections/dashboard'

export const getInspectionStatusFilterOptions = (t) => [
  {
    id: 0,
    text: t('internal-inspections:filters.all'),
    value: INSPECTION_STATUS.ALL,
  },
  {
    id: 1,
    text: t('internal-inspections:filters.upcoming'),
    value: INSPECTION_STATUS.UPCOMING,
  },
  {
    id: 2,
    text: t('internal-inspections:filters.overDue'),
    value: INSPECTION_STATUS.OVERDUE,
  },
  {
    id: 3,
    text: t('internal-inspections:filters.dueToday'),
    value: INSPECTION_STATUS.DUE_TODAY,
  },
  {
    id: 4,
    text: t('internal-inspections:filters.completed'),
    value: INSPECTION_STATUS.COMPLETED,
  },
]

export const getInspectionFrequencyOptions = (t) => [
  {
    id: 4,
    text: t('internal-inspections:filters.all'),
    value: '',
  },
  {
    id: 0,
    text: t('internal-inspections:filters.weekly'),
    value: 'weekly',
  },
  {
    id: 1,
    text: t('internal-inspections:filters.monthly'),
    value: 'monthly',
  },
  {
    id: 2,
    text: t('internal-inspections:filters.yearly'),
    value: 'yearly',
  },
  {
    id: 3,
    text: t('internal-inspections:filters.assignedByTrigger'),
    value: 'trigger',
  },
]

const getRecurrenceTypeOptions = (t) => [
  {
    id: 0,
    label: t('internal-inspections:recurrenceTypes.daily'),
    value: 'daily',
  },
  {
    id: 1,
    label: t('internal-inspections:recurrenceTypes.week'),
    value: 'weekly',
  },
  {
    id: 2,
    label: t('internal-inspections:recurrenceTypes.month'),
    value: 'monthly',
  },
  {
    id: 3,
    label: t('internal-inspections:recurrenceTypes.year'),
    value: 'yearly',
  },
]

export const getDaysOfWeekOptions = (t) => [
  {
    id: 1,
    label: t('internal-inspections:daysOfWeek.monday'),
    value: 'monday',
  },
  {
    id: 2,
    label: t('internal-inspections:daysOfWeek.tuesday'),
    value: 'tuesday',
  },
  {
    id: 3,
    label: t('internal-inspections:daysOfWeek.wednesday'),
    value: 'wednesday',
  },
  {
    id: 4,
    label: t('internal-inspections:daysOfWeek.thursday'),
    value: 'thursday',
  },
  {
    id: 5,
    label: t('internal-inspections:daysOfWeek.friday'),
    value: 'friday',
  },
  {
    id: 6,
    label: t('internal-inspections:daysOfWeek.saturday'),
    value: 'saturday',
  },
  {
    id: 0,
    label: t('internal-inspections:daysOfWeek.sunday'),
    value: 'sunday',
  },
]

const getTriggerConditions = (t) => [
  {
    id: 0,
    label: t('internal-inspections:triggerConditions.before'),
    value: 'before',
  },
  {
    id: 1,
    label: t('internal-inspections:triggerConditions.after'),
    value: 'after',
  },
]

const getTriggerTypes = (t) => [
  {
    id: 0,
    label: t('internal-inspections:triggerTypes.moveInDate'),
    value: 'move-in',
  },
  {
    id: 1,
    label: t('internal-inspections:triggerTypes.moveOutDate'),
    value: 'move-out',
  },
]

export const DEFAULT_QUESTION = {
  id: 'default',
  question: '',
}

export const DEFAULT_LOCATION = {
  id: 'default',
  name: '',
  description: '',
  questions: [],
  requirePhoto: false,
  requireRating: true,
  requireNote: false,
  listPosition: 0,
}

export const getDefaultRoom = (t) => ({
  id: 'default',
  name: t('internal-inspections:advancedSetup.newLocation'),
  listPosition: 0,
  roomItems: [],
})

export const DEFAULT_TEMPLATE = {
  id: 'default',
  templateName: '',
  name: '',
  attachments: [],
  description: '',
  noteRequired: false,
  photosRequired: false,
  advanced: false,
  guided: false,
  rooms: [],
}

export const DEFAULT_INSPECTION = {
  id: 'default',
  locationType: LOCATION_TYPES.UNIT,
  locationValue: [],
  frequency: TRIGGER_TYPES.TIME,
  recurrenceType: 'weekly',
  dayOfWeek: null,
  date: null,
  recurrenceEndDate: null,
  assigneeIds: [],
  triggerDays: 1,
  triggerType: 'move-in',
  triggerCondition: 'before',
  reviewRequired: false,
  phase: null,
}

export const DEFAULT_TEMPLATE_FIELDS = [
  'id',
  'templateName',
  'name',
  'attachments',
  'description',
  'noteRequired',
  'photosRequired',
  'rooms',
  'guided',
]

export const DEFAULT_INSPECTION_FIELDS = [
  'id',
  'name',
  'locationType',
  'locationValue',
  'frequency',
  'recurrenceType',
  'dayOfWeek',
  'date',
  'recurrenceEndDate',
  'assignees',
  'triggerDays',
  'triggerType',
  'triggerCondition',
  'reviewRequired',
]

const getInspectionStatusBadgeColor = (status) => {
  switch (status) {
    case INSPECTION_STATUS.UPCOMING:
      return 'blue'
    case INSPECTION_STATUS.OVERDUE:
      return 'red'
    case INSPECTION_STATUS.DUE_TODAY:
      return 'orange'
    case INSPECTION_STATUS.STARTED:
      return 'green'
    case INSPECTION_STATUS.COMPLETED:
      return 'gray'
    default:
      return 'gray'
  }
}

const getBadgeTextForStatus = (status, t) => {
  switch (status) {
    case INSPECTION_STATUS.UPCOMING:
      return t('internal-inspections:filters.upcoming')
    case INSPECTION_STATUS.STARTED:
      return t('internal-inspections:status.started')
    default:
      return startCase(status)
  }
}

const getFrequencyType = (recurrenceType, t) => {
  switch (recurrenceType) {
    case 'daily':
    case 'weekly':
    case 'monthly':
    case 'yearly':
      return t('internal-inspections:frequency.periodic')
    case 'trigger':
      return t('internal-inspections:filters.assignedByTrigger')
    case 'one-time':
      return t('internal-inspections:frequency.oneTime')
    default:
      return ''
  }
}

const getFrequencyText = (data, timezone, t) => {
  const { recurrenceType, date, triggerType, triggerDays, triggerCondition } = data

  switch (recurrenceType) {
    case 'daily':
      return t('internal-inspections:recurrenceTypes.daily')
    case 'weekly':
      return date
        ? t('internal-inspections:frequency.weeklyEvery', {
            day: formatTimezoneDate(date, timezone, 'EEE'),
          })
        : t('internal-inspections:filters.weekly')
    case 'monthly':
      return date
        ? t('internal-inspections:frequency.monthlyOnThe', {
            day: formatTimezoneDate(date, timezone, 'do'),
          })
        : t('internal-inspections:filters.monthly')
    case 'yearly':
      return t('internal-inspections:filters.yearly')
    case 'trigger':
      return t('internal-inspections:frequency.triggerSummary', {
        count: triggerDays,
        condition: triggerCondition,
        type: triggerType,
      })
    case 'one-time':
      return date
        ? formatTimezoneDate(date, timezone, 'MM/dd/yyyy')
        : t('internal-inspections:frequency.oneTime')
  }
}

const getNextDayOfWeekTimestamp = (dayOfWeek) => {
  const daysOfWeek = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']
  const dayIndex = daysOfWeek.indexOf(dayOfWeek)

  if (dayIndex < 0) return null

  const now = new Date()
  const nextDayOfWeek = startOfDay(nextDay(now, dayIndex))

  return nextDayOfWeek
}

export const getInspectionData = (values, building) => ({
  buildingId: building.id,
  name: values.name,
  locationType: values.locationType,
  locationValue: buildLocationValuePayload(values.locationType, values.locationValue),
  assigneeIds: values.assigneeIds,
  reviewRequired: values.reviewRequired,
  recurrenceType: values.recurrenceType,
  recurrenceEndDate: values.recurrenceEndDate
    ? fromZonedTime(
        startOfDay(values.recurrenceEndDate),
        building ? building.timezone : '',
      ).getTime()
    : null,
  ...(values.frequency === TRIGGER_TYPES.TIME
    ? {
        date: fromZonedTime(startOfDay(values.date), building ? building.timezone : '').getTime(),
      }
    : {
        triggerType: values.triggerType,
        triggerDays: values.triggerDays,
        triggerCondition: values.triggerCondition,
      }),
})

export const getTemplateData = (values) => {
  const templateData = {
    description: values.description,
    noteRequired: values.noteRequired,
    photosRequired: values.photosRequired,
    guided: values.guided,
    rooms: values.rooms,
    name: values.name,
    templateName: values.templateName,
  }

  if (values.attachments.length) {
    const hasNewFiles = values.attachments.some((attachment) => attachment instanceof File)
    if (hasNewFiles) {
      const attachments = values.attachments.map((attachment) => {
        return new File([attachment], attachment.name, { type: attachment.type })
      })
      templateData.attachments = attachments
    }
  }

  return templateData
}

export const dashboardColumns = ({ timezone }, t) => {
  return [
    {
      id: 'select',

      Header: ({ getToggleAllRowsSelectedProps }) => {
        const props = getToggleAllRowsSelectedProps()
        return <CheckboxField name="col-checkbox" {...props} />
      },

      Cell: ({ row }) => {
        const props = row.getToggleRowSelectedProps()
        return <CheckboxField name={`row-checkbox-${row.id}-${row.original.id}`} {...props} />
      },
    },
    {
      Header: t('internal-inspections:columns.inspectionName'),
      id: 'inspectionName',

      Cell: ({ row: { original } }) => (
        <div className="name-cell">
          <span>{original.name}</span>
          {original.reviewRequired && original.status === INSPECTION_STATUS.COMPLETED ? (
            <div data-tip data-for="warning-tip">
              <div className="warning">
                <Warning />
                <span>{t('internal-inspections:columns.needsReview')}</span>
              </div>
              <ReactTooltip
                className="tooltip"
                id="warning-tip"
                place="top"
                effect="solid"
                multiline
              >
                {t('internal-inspections:columns.needsReviewTooltip')}
              </ReactTooltip>
            </div>
          ) : null}
        </div>
      ),
    },
    {
      Header: t('internal-inspections:columns.location'),
      id: 'location',

      Cell: ({ row: { original } }) => {
        const { locationType, locationValue } = original
        return `${startCase(locationType)} ${locationValue}`
      },
    },
    {
      Header: t('internal-inspections:columns.assignees'),
      accessor: 'assignees',

      Cell: ({ value = [] }) => {
        const assignee = value[0]
        if (!assignee) return null
        return (
          <div className="assignee-cell">
            <img src={getAssetLink({ path: 'chitown-avatar/avatar', file: assignee.avatar })} />
            <span>{`${assignee.firstName} ${assignee.lastName}`}</span>
            {value.length - 1 > 0 ? <Badge color="gray">+{value.length - 1}</Badge> : null}
          </div>
        )
      },
    },
    {
      Header: t('internal-inspections:columns.scheduledCompletionDate'),
      accessor: 'date',

      Cell: ({ value }) => (value ? formatTimezoneDate(value, timezone, 'MM/dd/yyyy') : '-'),
    },
    {
      Header: t('internal-inspections:columns.status'),
      accessor: 'status',

      Cell: ({ value }) => (
        <Badge color={getInspectionStatusBadgeColor(value)}>
          {getBadgeTextForStatus(value, t)}
        </Badge>
      ),
    },
  ]
}

export const recurringColumns = ({ timezone, onSwitch = () => {} }, t) => {
  return [
    {
      id: 'select',

      Header: ({ getToggleAllRowsSelectedProps }) => {
        const props = getToggleAllRowsSelectedProps()
        return <CheckboxField name="col-checkbox" {...props} />
      },

      Cell: ({ row }) => {
        const props = row.getToggleRowSelectedProps()
        return <CheckboxField name={`row-checkbox-${row.id}-${row.original.id}`} {...props} />
      },
    },
    {
      Header: t('internal-inspections:columns.inspectionName'),
      accessor: 'name',
    },
    {
      Header: t('internal-inspections:columns.location'),
      id: 'location',

      Cell: ({ row: { original } }) => {
        const { locationType } = original
        return <Badge color="gray">{startCase(locationType)}</Badge>
      },
    },
    {
      Header: t('internal-inspections:columns.assignees'),
      accessor: 'assignees',

      Cell: ({ value = [] }) => {
        const assignee = value[0]
        if (!assignee) return null
        return (
          <div className="assignee-cell">
            <img src={getAssetLink({ path: 'chitown-avatar/avatar', file: assignee.avatar })} />
            <span>{`${assignee.firstName} ${assignee.lastName}`}</span>
            {value.length - 1 > 0 ? <Badge color="gray">+{value.length - 1}</Badge> : null}
          </div>
        )
      },
    },
    {
      Header: t('internal-inspections:columns.frequency'),
      id: 'frequency',

      Cell: ({ row: { original } }) => {
        const { recurrenceType } = original
        const type = getFrequencyType(recurrenceType, t)
        const text = getFrequencyText(original, timezone, t)
        return (
          <span>
            {type}
            <span className="light">({text})</span>
          </span>
        )
      },
    },
    {
      id: 'activity_switch',
      Header: t('internal-inspections:columns.active'),

      Cell: ({ row: { original } }) => {
        const { enabled, id } = original
        const [active, setActive] = useState(enabled)

        useEffect(() => {
          if (enabled !== active) {
            onSwitch(id, active)
          }
        }, [active])

        const text = active
          ? t('internal-inspections:columns.inspectionEnabledTooltip')
          : t('internal-inspections:columns.inspectionDisabledTooltip')

        return (
          <div data-tip data-for={`info-tip-${id}`} onClick={(e) => e.stopPropagation()}>
            <ToggleField
              id={`${id}`}
              name="table-toggle"
              onChange={() => setActive(!active)}
              value={active}
            />
            <ReactTooltip
              className="tooltip"
              id={`info-tip-${id}`}
              place="top"
              effect="solid"
              multiline
            >
              {text}
            </ReactTooltip>
          </div>
        )
      },
    },
  ]
}

export const templatesColumns = ({ timezone }, t) => {
  return [
    {
      id: 'select',

      Header: ({ getToggleAllRowsSelectedProps }) => {
        const props = getToggleAllRowsSelectedProps()
        return <CheckboxField name="col-checkbox" {...props} />
      },

      Cell: ({ row }) => {
        const props = row.getToggleRowSelectedProps()
        return <CheckboxField name={`row-checkbox-${row.id}-${row.original.id}`} {...props} />
      },
    },
    {
      Header: t('internal-inspections:columns.templateName'),
      accessor: 'templateName',
      Cell: ({ row: { original } }) => original.templateName || original.name,
    },
    {
      Header: t('internal-inspections:columns.created'),
      accessor: 'createdAt',
      Cell: ({ value }) => formatTimezoneDate(value, timezone, 'MM/dd/yyyy h:mm a'),
    },
    {
      Header: t('internal-inspections:columns.modified'),
      accessor: 'updatedAt',
      Cell: ({ value }) => formatTimezoneDate(value, timezone, 'MM/dd/yyyy h:mm a'),
    },
    {
      Header: t('internal-inspections:columns.source'),
      accessor: 'building',

      Cell: ({ value }) => (
        <div className="name-cell">
          <img src={getAssetLink({ path: 'chitown-building', file: value.logo })} />
          <span>{value.name}</span>
        </div>
      ),
    },
  ]
}

export const getInspectionLocationInput = (
  values,
  handleChange = () => {},
  setFieldValue = () => {},
  {
    fetchUnits = () => {},
    unitOptions = [],
    allUnitsLoaded,
    unitTypes = [],
    singleUnitOnly = false,
  } = {},
  t,
) => {
  switch (values.locationType) {
    case LOCATION_TYPES.UNIT: {
      // Scheduled inspection instances are single-unit by definition and save through
      // PUT /inspection/scheduled/:id, which only accepts a string locationValue.
      if (singleUnitOnly) {
        return (
          <SelectDropdownInput
            placeholder="Search by unit"
            data={unitOptions}
            onSelectOption={(item) => setFieldValue('locationValue', item.value)}
            selectedValueLabel={values.locationValue}
            fetchData={(...args) => fetchUnits(values, ...args)}
            showSelectedInDropdown
            isLoadedAll={allUnitsLoaded}
          />
        )
      }

      const selectedUnits = toUnitArray(values.locationValue)
      return (
        <Select
          placeholder={t('internal-inspections:locationInput.searchByUnit')}
          className="search-select-container"
          classNamePrefix="select-primary"
          options={getUnitSelectOptions(t, unitOptions, selectedUnits)}
          isMulti
          closeMenuOnSelect={false}
          components={{ MultiValue: UnitMultiValue }}
          hiddenUnitsCount={Math.max(selectedUnits.length - MAX_VISIBLE_UNIT_CHIPS, 0)}
          value={selectedUnits.map(toUnitOption)}
          onChange={(items) =>
            setFieldValue('locationValue', resolveUnitSelection(items, unitOptions))
          }
        />
      )
    }
    case LOCATION_TYPES.COMMON_AREA:
    case LOCATION_TYPES.OTHER:
      return (
        <TextField
          name="locationValue"
          onChange={handleChange}
          value={values.locationValue}
          placeholder={t('internal-inspections:locationInput.enterNamePlaceholder', {
            type: startCase(values.locationType),
          })}
        />
      )
    case LOCATION_TYPES.UNIT_TYPES: {
      const value =
        values.locationValue && Array.isArray(values.locationValue) && values.locationValue.length
          ? values.locationValue.map((name) => unitTypes.find((type) => type.name === name))
          : []
      const unitTypeOptions = [
        { id: 0, text: 'Select All', name: '__select_all__', selectAll: true },
        ...unitTypes,
      ]
      return (
        <Filter
          options={unitTypeOptions}
          placeholder={
            values.locationValue && values.locationValue.length
              ? values.locationValue.join(', ')
              : t('internal-inspections:locationInput.selectUnitTypesPlaceholder')
          }
          value={values.locationValue ? value : []}
          name="locationValue"
          onChange={(e) => {
            const names = e.target.value.map(({ name }) => name)
            setFieldValue('locationValue', names)
          }}
          config={{ multiple: true, hasSearch: true }}
        />
      )
    }
    default:
      return null
  }
}

export const getInspectionFrequencyFields = (
  values,
  handleChange,
  setFieldValue,
  errors = {},
  t,
) => {
  switch (values.frequency) {
    case TRIGGER_TYPES.TIME:
      return (
        <React.Fragment>
          <div>
            <span className="input-label">
              {t('internal-inspections:frequencyFields.repeatEvery')}
            </span>
            <SelectField
              name="recurrenceType"
              options={getRecurrenceTypeOptions(t)}
              placeholder={t('internal-inspections:frequencyFields.repeatEveryPlaceholder')}
              onChange={(e) => {
                handleChange(e)
                setFieldValue('date', null)
                setFieldValue('dayOfWeek', null)
              }}
              value={values.recurrenceType}
            />
          </div>
          {values.recurrenceType === 'weekly' ? (
            <div>
              <span className="input-label">
                {t('internal-inspections:frequencyFields.dayOfWeek')}
              </span>
              <SelectField
                name="dayOfWeek"
                options={getDaysOfWeekOptions(t)}
                placeholder={t('internal-inspections:frequencyFields.selectDayOfWeekPlaceholder')}
                onChange={(e) => {
                  handleChange(e)
                  const nextDayOfWeek = getNextDayOfWeekTimestamp(e.target.value)
                  setFieldValue('date', nextDayOfWeek)
                }}
                isError={!!errors.date}
                value={values.dayOfWeek}
              />
            </div>
          ) : (
            <div>
              <span className="input-label">
                {t('internal-inspections:frequencyFields.startDate')}
              </span>
              <DateField
                name="date"
                placeholderText={t(
                  'internal-inspections:frequencyFields.selectStartDatePlaceholder',
                )}
                autoComplete="off"
                dateFormat="MM/dd/yyyy"
                minDate={new Date()}
                value={values.date ? new Date(values.date) : null}
                onChange={handleChange}
                onChangeRaw={(e) => e.preventDefault()}
                isError={!!errors.date}
                errorLabel={errors.date}
              />
            </div>
          )}
          <div>
            <span className="input-label">
              {t('internal-inspections:frequencyFields.endDate')}{' '}
              <span className="input-label--light">
                {t('internal-inspections:frequencyFields.optional')}
              </span>
            </span>
            <DateField
              name="recurrenceEndDate"
              placeholderText={t('internal-inspections:frequencyFields.selectDueDatePlaceholder')}
              autoComplete="off"
              dateFormat="MM/dd/yyyy"
              minDate={new Date()}
              value={values.recurrenceEndDate ? new Date(values.recurrenceEndDate) : null}
              onChange={handleChange}
              onChangeRaw={(e) => e.preventDefault()}
            />
          </div>
        </React.Fragment>
      )
    case TRIGGER_TYPES.TRIGGER:
    default:
      return (
        <div className="select-start-trigger">
          <span className="input-label">
            {t('internal-inspections:frequencyFields.selectTrigger')}
          </span>
          <div>
            <TextField
              name="triggerDays"
              type="number"
              onChange={handleChange}
              value={values.triggerDays}
              min={1}
            />
            <SelectField
              name="triggerCondition"
              options={getTriggerConditions(t)}
              onChange={handleChange}
              value={values.triggerCondition}
            />
            <SelectField
              name="triggerType"
              options={getTriggerTypes(t)}
              onChange={handleChange}
              value={values.triggerType}
            />
          </div>
        </div>
      )
  }
}

export const getDraggableStyle = (isDragging) => {
  return {
    marginBottom: 12,
    backgroundColor: isDragging ? '#FDF9F4' : '#f9f8f8',
  }
}

export const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list)
  const [removed] = result.splice(startIndex, 1)
  result.splice(endIndex, 0, removed)
  return result
}
